/**
 * Sidebar: the shadcn sidebar (provider state, icon rail, mobile sheet).
 *
 * `attachSidebar(wrapper)` owns the open/collapsed state of everything inside
 * a `sidebar-wrapper`:
 *
 * - **State** is `open` (`defaultOpen`, controlled `open` + `onOpenChange`),
 *   surfaced as `data-state=expanded|collapsed` on the wrapper and on each
 *   desktop `sidebar`, and `data-collapsible=offcanvas|icon` on a collapsed one
 *   (empty while expanded). `data-collapsible-mode` says which collapsing style
 *   the sidebar uses; `none` is never collapsed, never a sheet.
 * - **Toggling**: every `sidebar-trigger` and `sidebar-rail`, `toggle()`,
 *   `setOpen()`, and Ctrl/Cmd+B (`shortcut`, ignored in editable targets).
 * - **Persistence**: every change writes the shadcn cookie `sidebar_state`
 *   (`true|false`, `path=/`, 7 days) and, with `storageKey`, localStorage. On
 *   attach the stored value is restored synchronously, with transitions
 *   suppressed for that first paint, so the rail never animates from the markup
 *   state. Precedence: `open` option > storage > cookie > `defaultOpen` >
 *   the markup's `data-state` > expanded.
 *   **SSR on Workers**: read the cookie in the request handler with
 *   {@link parseSidebarCookie} and emit `data-state="expanded|collapsed"` (and
 *   `data-collapsible="<mode>"` when collapsed) on the wrapper's `sidebar`:
 *   the first server-rendered paint is then already correct, and attach
 *   agrees with it.
 * - **Mobile** (below `mobileBreakpoint`, 768px, via `matchMedia`, reactive):
 *   the sidebar's `sidebar-inner` moves into a `sheet` built at runtime and
 *   driven by `attachSheet` (overlay, focus trap and return, Escape, outside
 *   click, scroll lock, exit animation) from `openMobile` / `setOpenMobile` /
 *   `toggle()`. Leaving mobile closes the sheet and puts the inner back. The
 *   sheet is a `[data-slot="sheet"]` root holding `sheet-overlay.ui-sheet-overlay`
 *   and `sheet-content.ui-sheet-content.ui-sidebar-mobile[data-mobile=true]
 *   [data-sidebar=sidebar][data-side=left|right]` (portalled while open);
 *   `.ui-sidebar-mobile` carries the sidebar's own width and colours, with
 *   `--sidebar-width` pointed at `--sidebar-width-mobile`.
 * - **Menu**: `data-active="true"` on `sidebar-menu-button` and
 *   `sidebar-menu-sub-button` mirrors to `aria-current="page"`; `setActive()`
 *   moves it. A collapsed-to-icon desktop sidebar enables the `tooltip` roots
 *   around menu buttons (everything else sets `data-disabled` on them). Menu
 *   groups written as `collapsible` roots inside the sidebar are attached with
 *   `attachCollapsible`. A desktop `offcanvas` sidebar that is collapsed is
 *   `inert` so Tab never lands in it.
 *
 * CSS variables written on the wrapper when absent: `--sidebar-width`
 * (16rem), `--sidebar-width-icon` (3rem), `--sidebar-width-mobile` (18rem).
 *
 * @markup sidebar-wrapper div [ui-sidebar-wrapper] style="--sidebar-width:16rem;--sidebar-width-icon:3rem" (data-state=expanded|collapsed added)
 * @markup sidebar div [ui-sidebar group peer] data-state=expanded|collapsed data-collapsible=""|offcanvas|icon data-collapsible-mode=offcanvas|icon|none data-variant=sidebar|floating|inset data-side=left|right
 * @markup sidebar-gap div [ui-sidebar-gap]
 * @markup sidebar-container div [ui-sidebar-container] (inert while collapsed offcanvas)
 * @markup sidebar-inner div [ui-sidebar-inner] data-sidebar=sidebar (moves into the mobile sheet below the breakpoint)
 * @markup sidebar-mobile-content div [ui-sheet-content ui-sidebar-mobile] data-mobile=true data-sidebar=sidebar data-side=left|right (built by the primitive in the mobile sheet)
 * @markup sidebar-trigger button [ui-sidebar-trigger] (aria-expanded added)
 * @markup sidebar-rail button [ui-sidebar-rail] aria-label="Toggle Sidebar" tabindex=-1 (aria-expanded added)
 * @markup sidebar-inset main [ui-sidebar-inset]
 * @markup sidebar-header div [ui-sidebar-header]
 * @markup sidebar-content div [ui-sidebar-content]
 * @markup sidebar-footer div [ui-sidebar-footer]
 * @markup sidebar-group div [ui-sidebar-group]
 * @markup sidebar-group-label div [ui-sidebar-group-label] presentational; hidden while collapsed to icons
 * @markup sidebar-group-action button [ui-sidebar-group-action] presentational; hidden while collapsed to icons
 * @markup sidebar-group-content div [ui-sidebar-group-content] presentational
 * @markup sidebar-menu ul [ui-sidebar-menu]
 * @markup sidebar-menu-item li [ui-sidebar-menu-item]
 * @markup sidebar-menu-button button|a [ui-sidebar-menu-button] data-active=true|false data-size=default|sm|lg data-variant=default|outline (aria-current=page when active)
 * @markup sidebar-menu-action button [ui-sidebar-menu-action] data-show-on-hover? presentational; sibling after the menu button, hidden while collapsed to icons
 * @markup sidebar-menu-badge div [ui-sidebar-menu-badge] presentational; sibling after the menu button, hidden while collapsed to icons
 * @markup sidebar-menu-sub ul [ui-sidebar-menu-sub]
 * @markup sidebar-menu-sub-item li [ui-sidebar-menu-sub-item]
 * @markup sidebar-menu-sub-button a [ui-sidebar-menu-sub-button] data-active=true|false data-size=sm|md (aria-current=page when active)
 */
import { applyDisabled, createControllable } from './_layout-state.js';
import { ownedBy } from './_dom.js';
import { attachCollapsible, collapsibleOf } from './collapsible.js';
import { attachSheet } from './sheet.js';
export const SIDEBAR_COOKIE_NAME = 'sidebar_state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const WRAPPER = 'sidebar-wrapper';
/** Reads `sidebar_state` out of a `Cookie` request header (Workers SSR). */
export function parseSidebarCookie(header, name = SIDEBAR_COOKIE_NAME) {
    if (!header)
        return undefined;
    for (const pair of header.split(';')) {
        const [key, ...rest] = pair.trim().split('=');
        if (key !== name)
            continue;
        const value = rest.join('=');
        if (value === 'true')
            return true;
        if (value === 'false')
            return false;
    }
    return undefined;
}
/** The cookie string shadcn writes (`sidebar_state=true; path=/; max-age=604800`). */
export function serializeSidebarCookie(open, name = SIDEBAR_COOKIE_NAME, maxAge = SIDEBAR_COOKIE_MAX_AGE) {
    return `${name}=${open}; path=/; max-age=${maxAge}; SameSite=Lax`;
}
const EDITABLE = 'input, textarea, select, [contenteditable]:not([contenteditable="false"])';
export function attachSidebar(root, initial = {}) {
    let options = { ...initial };
    let destroyed = false;
    let mobileQuery = null;
    let isMobile = false;
    let openMobile = false;
    let first = true;
    let mobiles = [];
    const attachedCollapsibles = [];
    const activeMarked = new WeakSet();
    const cookieName = () => options.cookie ?? SIDEBAR_COOKIE_NAME;
    const desktopSidebars = () => Array.from(root.querySelectorAll('[data-slot="sidebar"]')).filter((sidebar) => ownedBy(root, WRAPPER, sidebar));
    const modeOf = (sidebar) => {
        const mode = options.collapsible ?? sidebar.getAttribute('data-collapsible-mode');
        return mode === 'icon' || mode === 'none' ? mode : 'offcanvas';
    };
    /** Parts in the wrapper plus those currently inside a mobile sheet (which may be portalled). */
    function parts(slot) {
        const found = new Set();
        const selector = `[data-slot="${slot}"]`;
        for (const part of root.querySelectorAll(selector)) {
            if (ownedBy(root, WRAPPER, part))
                found.add(part);
        }
        for (const mobile of mobiles) {
            for (const part of mobile.panel.querySelectorAll(selector))
                found.add(part);
        }
        return Array.from(found);
    }
    const owns = (node) => (root.contains(node) &&
        node instanceof Element &&
        node.closest(`[data-slot="${WRAPPER}"]`) === root) ||
        mobiles.some((mobile) => mobile.panel.contains(node));
    // --- persisted state ------------------------------------------------------
    function readPersisted() {
        if (options.storageKey) {
            try {
                const stored = globalThis.localStorage?.getItem(options.storageKey);
                if (stored === 'true')
                    return true;
                if (stored === 'false')
                    return false;
            }
            catch {
                // Storage is unavailable (privacy mode): fall through to the cookie.
            }
        }
        const name = cookieName();
        return name === false || typeof document === 'undefined'
            ? undefined
            : parseSidebarCookie(document.cookie, name);
    }
    function persist(open) {
        const name = cookieName();
        if (name !== false) {
            // biome-ignore lint/suspicious/noDocumentCookie: the Cookie Store API is async and missing from older Safari; the cookie must be written before the next navigation.
            document.cookie = serializeSidebarCookie(open, name, options.cookieMaxAge);
        }
        if (options.storageKey) {
            try {
                globalThis.localStorage?.setItem(options.storageKey, String(open));
            }
            catch {
                // Storage is unavailable; the cookie still carries the state.
            }
        }
    }
    const markupOpen = (() => {
        const state = desktopSidebars()[0]?.getAttribute('data-state');
        return state === 'collapsed' ? false : state === 'expanded' ? true : undefined;
    })();
    const state = createControllable(options.open ?? readPersisted() ?? options.defaultOpen ?? markupOpen ?? true, (next) => {
        persist(next);
        options.onOpenChange?.(next);
    });
    if (options.open !== undefined)
        state.control(options.open);
    // --- sync -----------------------------------------------------------------
    function setVar(name, value, fallback) {
        if (value !== undefined)
            root.style.setProperty(name, value);
        else if (!root.style.getPropertyValue(name))
            root.style.setProperty(name, fallback);
    }
    function syncDesktop() {
        const open = state.get();
        const name = open ? 'expanded' : 'collapsed';
        root.setAttribute('data-state', name);
        setVar('--sidebar-width', options.width, '16rem');
        setVar('--sidebar-width-icon', options.widthIcon, '3rem');
        setVar('--sidebar-width-mobile', options.widthMobile, '18rem');
        for (const sidebar of desktopSidebars()) {
            const mode = modeOf(sidebar);
            sidebar.setAttribute('data-collapsible-mode', mode);
            if (options.variant)
                sidebar.setAttribute('data-variant', options.variant);
            if (options.side)
                sidebar.setAttribute('data-side', options.side);
            if (mode === 'none')
                continue;
            sidebar.setAttribute('data-state', name);
            sidebar.setAttribute('data-collapsible', open ? '' : mode);
            const container = sidebar.querySelector('[data-slot="sidebar-container"]');
            if (container)
                container.inert = !open && mode === 'offcanvas' && !isMobile;
        }
    }
    function syncControls() {
        const expanded = String(isMobile ? openMobile : state.get());
        for (const slot of ['sidebar-trigger', 'sidebar-rail']) {
            for (const control of parts(slot))
                control.setAttribute('aria-expanded', expanded);
        }
    }
    function syncMenu() {
        const tooltipsOn = state.get() === false && !isMobile;
        for (const button of parts('sidebar-menu-button')) {
            const tooltip = button.closest('[data-slot="tooltip"]');
            if (tooltip)
                applyDisabled(tooltip, !tooltipsOn);
        }
        for (const slot of ['sidebar-menu-button', 'sidebar-menu-sub-button']) {
            for (const button of parts(slot)) {
                const target = button.hasAttribute('data-as-child') && button.firstElementChild
                    ? button.firstElementChild
                    : button;
                const active = button.getAttribute('data-active') === 'true' ||
                    target.getAttribute('data-active') === 'true';
                if (active) {
                    target.setAttribute('aria-current', 'page');
                    activeMarked.add(target);
                }
                else if (activeMarked.delete(target)) {
                    target.removeAttribute('aria-current');
                }
            }
        }
        if (options.collapsibles === false)
            return;
        for (const group of parts('collapsible')) {
            if (collapsibleOf(group))
                continue;
            const controller = attachCollapsible(group);
            attachedCollapsibles.push(() => controller.destroy());
        }
    }
    function syncAll() {
        const targets = first ? transitionTargets() : [];
        for (const target of targets)
            target.style.setProperty('transition', 'none', 'important');
        syncDesktop();
        syncControls();
        syncMenu();
        if (targets.length > 0) {
            void root.offsetWidth;
            for (const target of targets)
                target.style.removeProperty('transition');
        }
        first = false;
    }
    function transitionTargets() {
        return desktopSidebars().flatMap((sidebar) => Array.from(sidebar.querySelectorAll('[data-slot="sidebar-gap"], [data-slot="sidebar-container"], [data-slot="sidebar-rail"]')));
    }
    // --- mobile sheet ----------------------------------------------------------
    function buildMobile(sidebar) {
        const inner = sidebar.querySelector('[data-slot="sidebar-inner"]');
        if (!inner || modeOf(sidebar) === 'none')
            return null;
        const side = options.side ?? sidebar.getAttribute('data-side') ?? 'left';
        const sheetRoot = document.createElement('div');
        sheetRoot.setAttribute('data-slot', 'sheet');
        sheetRoot.style.display = 'contents';
        const overlay = document.createElement('div');
        overlay.setAttribute('data-slot', 'sheet-overlay');
        overlay.className = 'ui-sheet-overlay';
        const panel = document.createElement('div');
        panel.setAttribute('data-slot', 'sheet-content');
        panel.setAttribute('data-sidebar', 'sidebar');
        panel.setAttribute('data-mobile', 'true');
        panel.setAttribute('data-side', side);
        panel.setAttribute('aria-label', options.mobileLabel ?? 'Sidebar');
        panel.className = 'ui-sheet-content ui-sidebar-mobile';
        panel.style.setProperty('--sidebar-width', 'var(--sidebar-width-mobile)');
        const innerMarker = document.createComment('sidebar-inner');
        inner.before(innerMarker);
        panel.appendChild(inner);
        sheetRoot.append(overlay, panel);
        sidebar.after(sheetRoot);
        const sheet = attachSheet(sheetRoot, {
            open: openMobile,
            side: side === 'right' ? 'right' : 'left',
            onOpenChange: (open) => setOpenMobile(open),
        });
        return { inner, innerMarker, sheetRoot, overlay, panel, sheet };
    }
    function teardownMobile() {
        for (const mobile of mobiles) {
            mobile.sheet.destroy();
            mobile.innerMarker.replaceWith(mobile.inner);
            mobile.sheetRoot.remove();
        }
        mobiles = [];
    }
    function enterMobile() {
        mobiles = desktopSidebars().flatMap((sidebar) => buildMobile(sidebar) ?? []);
    }
    function setOpenMobile(open) {
        if (destroyed || !isMobile || open === openMobile)
            return;
        openMobile = open;
        for (const mobile of mobiles)
            mobile.sheet.setOpen(open);
        syncControls();
        options.onOpenMobileChange?.(open);
    }
    function setMobile(next) {
        if (next === isMobile)
            return;
        if (!next) {
            const wasOpen = openMobile;
            teardownMobile();
            isMobile = false;
            openMobile = false;
            if (wasOpen)
                options.onOpenMobileChange?.(false);
        }
        else {
            isMobile = true;
            enterMobile();
        }
        syncAll();
    }
    function watchViewport() {
        mobileQuery?.removeEventListener('change', onViewportChange);
        mobileQuery =
            typeof globalThis.matchMedia === 'function'
                ? globalThis.matchMedia(`(max-width: ${(options.mobileBreakpoint ?? 768) - 1}px)`)
                : null;
        mobileQuery?.addEventListener('change', onViewportChange);
        setMobile(mobileQuery?.matches ?? false);
    }
    function onViewportChange(event) {
        setMobile(event.matches);
    }
    // --- interaction ----------------------------------------------------------
    function setOpen(open) {
        if (destroyed)
            return;
        state.request(open);
        syncAll();
    }
    function toggle() {
        if (isMobile)
            setOpenMobile(!openMobile);
        else
            setOpen(!state.get());
    }
    function onClick(event) {
        const target = event.target;
        if (!(target instanceof Element) || !owns(target))
            return;
        const control = target.closest('[data-slot="sidebar-trigger"], [data-slot="sidebar-rail"]');
        if (control && owns(control)) {
            toggle();
            return;
        }
    }
    function onKeyDown(event) {
        const key = options.shortcut ?? 'b';
        if (key === false || event.key.toLowerCase() !== key.toLowerCase())
            return;
        if (!(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey)
            return;
        const target = event.target;
        if (target instanceof Element && target.closest(EDITABLE))
            return;
        event.preventDefault();
        toggle();
    }
    document.addEventListener('click', onClick);
    window.addEventListener('keydown', onKeyDown);
    watchViewport();
    syncAll();
    return {
        get isOpen() {
            return state.get();
        },
        get state() {
            return state.get() ? 'expanded' : 'collapsed';
        },
        get isMobile() {
            return isMobile;
        },
        get openMobile() {
            return openMobile;
        },
        setOpen,
        setOpenMobile,
        toggle,
        setActive(target) {
            if (destroyed)
                return;
            for (const slot of ['sidebar-menu-button', 'sidebar-menu-sub-button']) {
                for (const button of parts(slot)) {
                    const link = button.hasAttribute('data-as-child') && button.firstElementChild
                        ? button.firstElementChild
                        : button;
                    const match = typeof target === 'string'
                        ? link.getAttribute('href') === target
                        : target !== null && (target === button || target === link);
                    button.setAttribute('data-active', String(match));
                    if (link !== button)
                        link.setAttribute('data-active', String(match));
                }
            }
            syncMenu();
        },
        update(next) {
            if (destroyed)
                return;
            const breakpointChanged = next.mobileBreakpoint !== undefined && next.mobileBreakpoint !== options.mobileBreakpoint;
            options = { ...options, ...next };
            if (next.open !== undefined)
                state.control(next.open);
            if (breakpointChanged)
                watchViewport();
            syncAll();
        },
        refresh() {
            if (!destroyed)
                syncAll();
        },
        destroy() {
            if (destroyed)
                return;
            destroyed = true;
            document.removeEventListener('click', onClick);
            window.removeEventListener('keydown', onKeyDown);
            mobileQuery?.removeEventListener('change', onViewportChange);
            teardownMobile();
            for (const release of attachedCollapsibles.splice(0))
                release();
        },
    };
}
